import React, { useEffect, useState } from 'react';
import { C, ACCENT } from '../../theme';
import { digitalApi as api } from './api';
import { calcFlight, combineTot, mergePlanCatalogue, DEF_UNIVERSE } from './DigitalPlanningEngine';
import { Star } from 'lucide-react';

// Compare 2-3 plans (2026-10-05) — every number here is computed with the SAME calcFlight/
// combineTot the plan editor itself uses (both exported from DigitalPlanningEngine.js, imported
// directly, never reimplemented). The catalogue each plan's flights run against is resolved with
// the SAME `mergePlanCatalogue` the editor's own mount-time effect uses — NOT the raw
// `data.catalogue` array. Found live while verifying this page: a frozen catalogue entry is not a
// complete, self-sufficient snapshot in this codebase — layer 3 (the saved snapshot) only
// overwrites the fields it explicitly carries, so any field it omits silently falls through to the
// CURRENT live global/client values. Skipping this merge produced a real discrepancy against a
// plan's own KPI bar (reach) during verification; see CLAUDE.md for the exact mechanism.
//
// Engagement and Conversions are NOT fields calcFlight/combineTot produce at all (confirmed by
// inspection) — Conversions already has a real precedent (SummaryTab: `clicks * platform.cvr` per
// platform-line, summed) so this reuses that identical formula; Engagement has no existing
// computed-number precedent anywhere in the app, so it is defined the same way every other
// rate-to-absolute metric in this codebase already works (VTR->views is impr*vtr, CTR->clicks is
// impr*ctr, CVR->conversions is clicks*cvr) — Engagement = impr*engagement_rate, summed.

const MIN_PLANS = 2, MAX_PLANS = 3;

const fe   = n => (n==null||!isFinite(n)||isNaN(n)) ? '—' : '€'+Math.round(n).toLocaleString();
// CPM/CPC are typically sub-€1 figures (confirmed against real org 3 data via the Node harness —
// `fe` alone rounded every real CPM to "€0"/"€1", indistinguishable) — 2-decimal euro formatter,
// verbatim-matching DigitalPlanningEngine.js's own `fe2`, used for those 4 rows specifically.
const fe2  = n => (n==null||!isFinite(n)||isNaN(n)) ? '—' : '€'+n.toFixed(2);
const fmil = n => { if(n==null||!n||!isFinite(n)) return '—'; if(n>=1e6) return (n/1e6).toFixed(1)+'M'; if(n>=1000) return Math.round(n/1000)+'K'; return Math.round(n)+''; };
const fp   = n => (n==null||!isFinite(n)||isNaN(n)) ? '—' : (n*100).toFixed(1)+'%';
const fn   = n => (n==null||!isFinite(n)||isNaN(n)) ? '—' : Math.round(n).toLocaleString();

// Pure — takes calcFlight's own per-flight results (already computed) plus the flights/audiences
// arrays they came from, and derives every Compare-specific number. Exported and unit-tested
// standalone (computeComparePlanMetrics.test.js) with no need to run calcFlight itself (already
// covered by reach.test.js/calcFlightWeekAllocation.test.js) or touch the network.
export function deriveCompareMetrics(flights, audiencesList, flightResults) {
  const planTot = combineTot(flightResults.map(x => x.result));
  const nFlights = flights.length;

  const blendedCpm = planTot.impressions > 0 ? (planTot.budget / planTot.impressions) * 1000 : null;
  const meanCpm = nFlights ? flightResults.reduce((s, x) => s + (x.result.tot.ecpm || 0), 0) / nFlights : null;

  const nonZeroClickFlights = flightResults.filter(x => x.result.tot.clicks > 0);
  const zeroClickCount = nFlights - nonZeroClickFlights.length;
  const blendedCpc = planTot.clicks > 0 ? planTot.budget / planTot.clicks : null;
  const meanCpc = nonZeroClickFlights.length
    ? nonZeroClickFlights.reduce((s, x) => s + (x.result.tot.budget / x.result.tot.clicks), 0) / nonZeroClickFlights.length
    : null;

  const meanReach = nFlights ? flightResults.reduce((s, x) => s + (x.result.tot.reach || 0), 0) / nFlights : null;

  const totalConversions = flightResults.reduce((s, x) => s + x.conversions, 0);
  const totalEngagement = flightResults.reduce((s, x) => s + x.engagement, 0);
  const anyHasCvr = flightResults.some(x => x.hasCvr);
  const anyHasEngagementRate = flightResults.some(x => x.hasEngagementRate);

  const period = flights.length ? {
    start: flights.reduce((min, f) => (!min || (f.startDate && f.startDate < min)) ? f.startDate : min, null),
    end: flights.reduce((max, f) => (!max || (f.endDate && f.endDate > max)) ? f.endDate : max, null),
  } : null;
  const objectives = [...new Set(flights.map(f => f.objective).filter(Boolean))];
  const platformCount = new Set(flights.flatMap(f => (f.platforms || []).map(pe => pe.platformId))).size;
  const audienceNames = [...new Set(flights.map(f => {
    if (!f.audienceId) return 'Default universe';
    const a = audiencesList.find(x => x.id === f.audienceId);
    return a ? a.name : 'Unknown audience';
  }))];

  return {
    period, audienceNames, objectives, platformCount, nFlights,
    planTot, blendedCpm, meanCpm, blendedCpc, meanCpc, zeroClickCount,
    meanReach, totalConversions, totalEngagement, anyHasCvr, anyHasEngagementRate,
  };
}

// Per-flight conversions/engagement — see the module-level comment for why these two formulas
// exist (one matches SummaryTab's existing precedent, one is newly defined but convention-matched).
export function deriveFlightExtras(calcFlightResult) {
  let conversions = 0, engagement = 0, hasCvr = false, hasEngagementRate = false;
  (calcFlightResult.platforms || []).forEach(pr => {
    if (pr.p?.cvr) { conversions += pr.clicks * pr.p.cvr; hasCvr = true; }
    if (pr.p?.engagement_rate) { engagement += pr.impr * pr.p.engagement_rate; hasEngagementRate = true; }
  });
  return { conversions, engagement, hasCvr, hasEngagementRate };
}

async function loadPlanComparison(planId, globalPlatforms, sitesMap, siteOverlaps, trackerEligibility) {
  const plan = await api.getPlan(planId);
  const [brand, client, clientOverrides] = await Promise.all([
    plan.brand_id ? api.getBrand(plan.brand_id).catch(() => null) : null,
    plan.client_id ? api.getClient(plan.client_id).catch(() => null) : null,
    plan.client_id ? api.getClientCatalogueOverrides(plan.client_id).catch(() => ({})) : Promise.resolve({}),
  ]);
  const data = plan.data || {};
  const flights = data.flights || [];
  const audiencesList = data.audiences || [];
  // Same 3-layer merge the editor itself uses — see the module comment above for why the raw
  // frozen `data.catalogue` array alone is not enough.
  const { catalogue } = mergePlanCatalogue(globalPlatforms, clientOverrides, data.catalogue || []);
  const trackersCatalogue = data.trackersCatalogue || [];
  const universe = data.universe || DEF_UNIVERSE;

  const flightResults = flights.map(f => {
    const aud = f.audienceId ? audiencesList.find(a => a.id === f.audienceId) : null;
    const r = calcFlight(f, catalogue, aud ? aud.universe : universe, aud || null, sitesMap, siteOverlaps, trackersCatalogue, trackerEligibility);
    return { flight: f, result: r, ...deriveFlightExtras(r) };
  });

  const metrics = deriveCompareMetrics(flights, audiencesList, flightResults);

  return {
    id: plan.id, name: plan.name, status: plan.status,
    clientId: plan.client_id, clientName: client?.name || null, brandName: brand?.name || null,
    universe, ...metrics,
  };
}

export function headerDiffers(plans, key) {
  if (plans.length < 2) return false;
  const vals = plans.map(p => JSON.stringify(p[key]));
  return new Set(vals).size > 1;
}

function DeltaBadge({ value, baseline, fmt }) {
  if (baseline == null || value == null || baseline === 0) return null;
  const abs = value - baseline;
  const pct = (abs / Math.abs(baseline)) * 100;
  if (Math.abs(abs) < 1e-9) return <span style={{ fontSize: 10, color: '#8aa0b8', marginLeft: 6 }}>(baseline)</span>;
  const sign = abs > 0 ? '+' : '';
  const color = abs > 0 ? '#2a8a5a' : '#a02020';
  return <span style={{ fontSize: 10, color, marginLeft: 6 }}>({sign}{fmt(abs).replace('—', '0')} / {sign}{pct.toFixed(1)}%)</span>;
}

function Row({ label, plans, getValue, fmt, markBest, higherIsBetter, note, tooltip }) {
  const values = plans.map(getValue);
  const numeric = values.map(v => (typeof v === 'number' ? v : null));
  let bestIdx = -1;
  if (markBest && numeric.some(v => v != null)) {
    const candidates = numeric.map((v, i) => [v, i]).filter(([v]) => v != null);
    if (candidates.length) {
      candidates.sort((a, b) => higherIsBetter ? b[0] - a[0] : a[0] - b[0]);
      bestIdx = candidates[0][1];
    }
  }
  return (
    <tr style={C.tr}>
      <td style={{ ...C.td, fontWeight: 600 }} title={tooltip}>{label}{note ? <div style={{ fontSize: 10, color: '#8aa0b8', fontWeight: 400 }}>{note}</div> : null}</td>
      {values.map((v, i) => (
        <td key={i} style={{ ...C.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          {bestIdx === i && <Star size={11} style={{ color: '#d08000', verticalAlign: 'middle', marginRight: 4 }} />}
          {fmt(v)}
          {i > 0 && typeof v === 'number' && typeof numeric[0] === 'number' && <DeltaBadge value={v} baseline={numeric[0]} fmt={fmt} />}
        </td>
      ))}
    </tr>
  );
}

function PlanPicker({ orgPlans, brandsById, clientsById, selected, setSelected, onCompare, loading }) {
  const [search, setSearch] = useState('');
  const filtered = orgPlans.filter(p => {
    if (!search) return true;
    const brand = brandsById[p.brand_id];
    const hay = `${p.name} ${brand?.name || ''} ${clientsById[brand?.client_id]?.name || ''}`.toLowerCase();
    return hay.includes(search.toLowerCase());
  });
  function toggle(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else { if (next.size >= MAX_PLANS) return prev; next.add(id); }
      return next;
    });
  }
  return (
    <div>
      <div style={{ fontSize: 12, color: '#8aa0b8', marginBottom: 14 }}>Select {MIN_PLANS} to {MAX_PLANS} plans to compare (same organization only).</div>
      <input placeholder="Search by plan, brand or client…" value={search} onChange={e => setSearch(e.target.value)}
        style={{ padding: '8px 14px', border: '1px solid #c8d8e8', borderRadius: 8, fontSize: 13, outline: 'none', width: 320, marginBottom: 14, background: 'rgba(255,255,255,0.7)' }} />
      <div style={{ maxHeight: 420, overflowY: 'auto', border: '1px solid #dce8f0', borderRadius: 10 }}>
        {filtered.map(p => {
          const brand = brandsById[p.brand_id];
          const client = brand ? clientsById[brand.client_id] : null;
          return (
            <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderBottom: '1px solid #eef4f8', cursor: 'pointer', background: selected.has(p.id) ? `${ACCENT}10` : 'transparent' }}>
              <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} disabled={!selected.has(p.id) && selected.size >= MAX_PLANS} style={{ accentColor: ACCENT }} />
              <span style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</span>
              <span style={C.mutedLabel()}>{p.status}</span>
              <span style={{ fontSize: 11, color: '#8aa0b8', marginLeft: 'auto' }}>{client?.name || ''}{brand ? ` / ${brand.name}` : ''}</span>
            </label>
          );
        })}
        {filtered.length === 0 && <div style={{ padding: 20, color: '#8aa0b8', fontSize: 12 }}>No plans match.</div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16 }}>
        <button style={C.btnPrimary} disabled={selected.size < MIN_PLANS || selected.size > MAX_PLANS || loading} onClick={onCompare}>
          {loading ? 'Loading…' : `Compare (${selected.size})`}
        </button>
        {selected.size > MAX_PLANS && <span style={{ ...C.feedback('danger'), marginBottom: 0, padding: '4px 10px' }}>Select at most {MAX_PLANS} plans.</span>}
      </div>
    </div>
  );
}

export default function ComparePlansPage() {
  const [orgPlans, setOrgPlans] = useState([]);
  const [brandsById, setBrandsById] = useState({});
  const [clientsById, setClientsById] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [compared, setCompared] = useState(null); // array of loadPlanComparison() results, in selection order
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([api.getPlans(), api.getBrands(), api.getClients()]).then(([plans, brands, clients]) => {
      setOrgPlans(plans.filter(p => !p.archived));
      setBrandsById(Object.fromEntries(brands.map(b => [b.id, b])));
      setClientsById(Object.fromEntries(clients.map(c => [c.id, c])));
    }).catch(() => {});
  }, []);

  async function handleCompare() {
    setLoading(true); setError('');
    try {
      const [globalPlatforms, sitesMap, siteOverlaps, trackerEligibility] = await Promise.all([
        api.getPlatforms(false),
        api.getSites().then(sites => {
          const m = {};
          (sites || []).forEach(s => { m[s.id] = { monthly_real_users_total: s.monthly_real_users_total || 0, monthly_real_users_desktop: s.monthly_real_users_desktop || 0, monthly_real_users_mobile: s.monthly_real_users_mobile || 0, monthly_real_users_app: s.monthly_real_users_app || 0 }; });
          return m;
        }),
        api.getSiteOverlaps(),
        api.getTrackerEligibility(),
      ]);
      const ids = [...selected];
      const results = [];
      for (const id of ids) results.push(await loadPlanComparison(id, globalPlatforms, sitesMap, siteOverlaps, trackerEligibility));
      setCompared(results);
    } catch (e) {
      setError(e.message || 'Failed to load comparison');
    } finally {
      setLoading(false);
    }
  }

  const diffs = compared ? {
    period: headerDiffers(compared, 'period'),
    audienceNames: headerDiffers(compared, 'audienceNames'),
    universe: headerDiffers(compared, 'universe'),
  } : null;

  return (
    <div style={C.page}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={C.title}>Compare Plans</h1>
        <div style={{ fontSize: 12, color: '#8aa0b8', marginTop: 4 }}>Select 2-3 plans to compare their computed KPIs side by side</div>
      </div>

      {!compared && (
        <PlanPicker orgPlans={orgPlans} brandsById={brandsById} clientsById={clientsById} selected={selected} setSelected={setSelected} onCompare={handleCompare} loading={loading} />
      )}
      {error && <div style={{ ...C.feedback('danger'), marginTop: 14 }}>{error}</div>}

      {compared && (
        <div>
          <button style={{ ...C.btnSecondary, marginBottom: 20 }} onClick={() => setCompared(null)}>← Change selection</button>

          {/* Header block per plan */}
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${compared.length}, 1fr)`, gap: 16, marginBottom: 16 }}>
            {compared.map((p, i) => (
              <div key={p.id} style={{ padding: 16, background: 'rgba(255,255,255,0.75)', border: '1px solid #dce8f0', borderRadius: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#1a2a3a', marginBottom: 6 }}>{p.name} {i === 0 && <span style={C.mutedLabel()}>baseline</span>}</div>
                <div style={{ fontSize: 11, color: '#6a8aaa', lineHeight: 1.7 }}>
                  <div>Client: {p.clientName || '—'}</div>
                  <div>Brand: {p.brandName || '—'}</div>
                  <div>Period: {p.period ? `${p.period.start} → ${p.period.end}` : '—'}</div>
                  <div>Audience: {p.audienceNames.join(', ') || '—'}</div>
                  <div>Universe: {p.universe.toLocaleString()}</div>
                  <div>Objectives: {p.objectives.join(', ') || '—'}</div>
                  <div>Platforms: {p.platformCount}</div>
                </div>
              </div>
            ))}
          </div>
          {(diffs.period || diffs.audienceNames || diffs.universe) && (
            <div style={{ ...C.feedback('warning'), marginBottom: 20 }}>
              These plans differ in {[diffs.period && 'period', diffs.audienceNames && 'audience', diffs.universe && 'universe'].filter(Boolean).join(', ')} — figures below are not a like-for-like comparison.
            </div>
          )}

          <table style={C.table}>
            <thead><tr>
              <th style={C.th}>Metric</th>
              {compared.map((p, i) => <th key={p.id} style={{ ...C.th, textAlign: 'right' }}>{p.name}{i === 0 ? ' (baseline)' : ''}</th>)}
            </tr></thead>
            <tbody>
              <Row label="Budget" plans={compared} getValue={p => p.planTot.budget} fmt={fe} />
              <Row label="Flights" plans={compared} getValue={p => p.nFlights} fmt={fn} />
              <Row label="Impressions" plans={compared} getValue={p => p.planTot.impressions} fmt={fmil} />
              <Row label="CPM (blended)" plans={compared} getValue={p => p.blendedCpm} fmt={fe2} markBest higherIsBetter={false} note="budget ÷ impressions × 1,000" />
              <Row label="CPM (mean of flights)" plans={compared} getValue={p => p.meanCpm} fmt={fe2} note="mean of each flight's own eCPM" />
              <Row label="CPC (blended)" plans={compared} getValue={p => p.blendedCpc} fmt={fe2} markBest higherIsBetter={false} note="budget ÷ clicks" />
              <Row label="CPC (mean of flights)" plans={compared}
                getValue={p => p.meanCpc}
                fmt={fe2}
                note={compared.some(p => p.zeroClickCount > 0) ? `excludes zero-click flights (${compared.map(p => p.zeroClickCount).join('/')} excluded)` : 'mean of flights with clicks'} />
              <Row label="Reach (plan vRCH%)" plans={compared} getValue={p => p.planTot.reach} fmt={fp} markBest higherIsBetter note="Sainsbury-folded across flights (combineTot)" />
              <Row label="Reach (mean of flights)" plans={compared} getValue={p => p.meanReach} fmt={fp} note="mean of each flight's own vRCH%" />
              <Row label="Clicks" plans={compared} getValue={p => p.planTot.clicks} fmt={fmil} markBest higherIsBetter />
              <Row label="Views" plans={compared} getValue={p => p.planTot.views} fmt={fmil} />
              <Row label="Engagement" plans={compared}
                getValue={p => p.anyHasEngagementRate ? p.totalEngagement : null}
                fmt={v => v == null ? '—' : fmil(v)}
                tooltip="No platform in this plan has an Engagement Rate set" />
              <Row label="Conversions" plans={compared}
                getValue={p => p.anyHasCvr ? p.totalConversions : null}
                fmt={v => v == null ? '—' : fmil(v)}
                tooltip="No platform in this plan has a CVR set" />
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
